import { ReactElement } from 'react'
import styled from 'styled-components'
import { LaborItem } from '../../../../../../../src/store/models/LaborModel'
import { Translate2 } from 'common'
import { Price } from '../../../../../../../src/ui-components'
import { SectionTitle, ReceiptTableGrid, ReceiptCell } from '../printStyles'

interface Props {
  laborItems: LaborItem[]
}

const getLaborRate = (labor: LaborItem): number => {
  if (labor.rate > 0) {
    return labor.rate
  }
  const parsedRate = Number(labor.laborRate)
  if (!Number.isNaN(parsedRate) && parsedRate > 0) {
    return parsedRate
  }
  return 0
}

const calculateLaborCost = (labor: LaborItem): number => {
  return getLaborRate(labor) * Number(labor.hours)
}

const Labor = (props: Props): ReactElement | null => {
  const { laborItems } = props

  if (!laborItems?.length) {
    return null
  }

  const laborTotal = laborItems.reduce(
    (sum, labor) => sum + calculateLaborCost(labor),
    0
  )

  const displayLaborRate = (labor: LaborItem): string => {
    if (labor.laborRate) {
      return labor.laborRate
    }
    const rate = getLaborRate(labor)
    return rate > 0 ? String(rate) : ''
  }

  return (
    <>
      <SectionTitle>{Translate2('labor')}</SectionTitle>
      <ReceiptTableGrid $columns="0.7fr 2fr 0.5fr 0.5fr 0.6fr 0.6fr 0.6fr">
        <ReceiptCell $header $align="start">
          {Translate2('laborId')}
        </ReceiptCell>
        <ReceiptCell $header $align="start">
          {Translate2('description')}
        </ReceiptCell>
        <ReceiptCell $header $align="end">
          {Translate2('rate')}
        </ReceiptCell>
        <ReceiptCell $header $align="end">
          {Translate2('hours')}
        </ReceiptCell>
        <ReceiptCell $header $align="end">
          {Translate2('wtyHours')}
        </ReceiptCell>
        <ReceiptCell $header $align="end">
          {Translate2('skillLevel')}
        </ReceiptCell>
        <ReceiptCell $header $align="end">
          {Translate2('total')}
        </ReceiptCell>

        {laborItems.map((labor) => (
          <LaborRow key={labor.laborId}>
            <ReceiptCell $align="start">{labor.laborId}</ReceiptCell>
            <ReceiptCell $align="start">
              {labor.laborDescription}
              {labor.details && ` - ${labor.details}`}
            </ReceiptCell>
            <ReceiptCell $align="end">{displayLaborRate(labor)}</ReceiptCell>
            <ReceiptCell $align="end">{labor.hours}</ReceiptCell>
            <ReceiptCell $align="end">{labor.warrantyHrs}</ReceiptCell>
            <ReceiptCell $align="end">{labor.skillLevel}</ReceiptCell>
            <ReceiptCell $align="end">
              <Price num={calculateLaborCost(labor)} />
            </ReceiptCell>
          </LaborRow>
        ))}
      </ReceiptTableGrid>
      <LaborTotalRow>
        <LaborTotalLabel>{Translate2('laborCost')}:</LaborTotalLabel>
        <LaborTotalValue>
          <Price num={laborTotal} />
        </LaborTotalValue>
      </LaborTotalRow>
    </>
  )
}

const LaborRow = styled.div`
  display: contents;
`

const LaborTotalRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 4px;
  font-size: 11pt;
  font-weight: 700;
  padding: 4px 6px;
`

const LaborTotalLabel = styled.span``

const LaborTotalValue = styled.span``

export default Labor
